








import {
  Vue,
  Component,
  Prop,
} from "@/shared/vuepack";
import _ from "lodash";
import moment from "moment";
import { formatCurrency } from "@/shared/currency";
import { GChart } from "vue-google-charts";
import { useThemeStore } from "@/shared/store/theme";


interface ChartDataItem {
  balance: number;
  date: string;
  timestamp?: number;
  transactionSummaries: { amount: number, description: string }[];
}

@Component({
  components: {
    GChart
  }
})
export default class PocGLineChart extends Vue {

  themeStore = useThemeStore();
  manualXAxisLabelCutoff = 7;

  @Prop()
  strokeColor: number;

  @Prop()
  fillColor: string;

  @Prop()
  displayPoints: boolean;

  @Prop()
  width: number;

  @Prop()
  height: number;

  @Prop()
  dataSet: ChartDataItem[];

  @Prop()
  predictedDataSet: ChartDataItem[];

  predictiveStrokeColor = "#B2BCBB";

  get hasPredictedData (): boolean {
    return !_.isEmpty(this.predictedDataSet);
  }

  get getChartOptions (): any {
    return {
      fontName: this.themeStore.fontFace,
      annotations: {
        color: this.themeStore.tertiaryColor
      },
      titlePosition: "none",
      colors: [ this.predictiveStrokeColor ],
      tooltip: {
        isHtml: true,
        // ignoreBounds: true,
      },
      chartArea: {
        height: "78%",
      },
      hAxis: {
        format: "M/dd/yy",
        baselineColor: "black",
        gridlines: {
          color: "transparent"
        },
        ticks: this.xAxisLabels
      },
      vAxis: {
        minValue: 1,
        baseline: 0,
        baselineColor: "black",
        format: "currency",
        viewWindow: {
          min: 1
        },
        gridlines: {
          color: "transparent",
        },
        minorGridlines: {
          color: "transparent",
        }
      },
      pointSize: (this.displayPoints ? 8 : 0),
      height: this.height,
      legend: { position: "none" },
      enableInteractivity: true,
    };
  }

  getPredictionDetailMarkup (balance: number, description: string) {
    if (balance) {
      return `<div margin-bottom: 3px;">
       ${description}
      </div>
      <div style="color: ${this.themeStore.tertiaryColor}; font-weight: bold;">
        ${formatCurrency(balance)}
      </div>`;
    }

    return "";
  }

  getTransactionBreakdownMarkup (transactionSummaries: { amount: number, description: string }[]) {
    let markup = "";

    if (transactionSummaries) {
      _.forEach(transactionSummaries, transactionSummary => {
        markup += `<div margin-bottom: 3px;">
        ${transactionSummary.description}
        </div>
        <div style="color: ${this.themeStore.tertiaryColor}; font-weight: bold;">
          ${formatCurrency(transactionSummary.amount)}
        </div>`;
      });
    }

    return markup;
  }

  tooltipFormatter (data: {
    balance: number, date: string, employerAmount?: number,
    employeeRecurringAmount?: number, employeeOneTimeAmount?: number,
    transactionSummaries: { amount: number, description: string }[]
  }): string {

    const mainMarkup = `<div style="font-weight: bold; margin-bottom: 3px;">
                          ${moment(data.date, "DD-MMM-YY").format("MMM DD")}
                        </div>
                        <div margin-bottom: 3px;">
                          Balance:
                        </div>
                        <div style="color: ${this.themeStore.tertiaryColor}; font-weight: bold;">
                          ${formatCurrency(data.balance)}
                        </div>`;

    const tooltipHtml = `<div style="padding: 2px 10px; min-width: 120px;">
                          ${mainMarkup}
                          ${this.getTransactionBreakdownMarkup(data.transactionSummaries)}
                          ${this.getPredictionDetailMarkup(data.employerAmount, "Employer Payroll")}
                          ${this.getPredictionDetailMarkup(data.employeeRecurringAmount, "Employee Payroll")}
                          ${this.getPredictionDetailMarkup(data.employeeOneTimeAmount, "Payroll One Time")}
                        </div>`;

    return tooltipHtml;
  }

  formatChartDataItem (balanceItem: ChartDataItem): any {

    const itemDate = moment(balanceItem.date, "DD-MMM-YY");
    const dateNow = moment();
    const isCurrentOrPast = itemDate.diff(dateNow) < 0 ? true : false;
    const fillOpacity = (isCurrentOrPast && this.fillColor) ? 0.5 : 0;

    return [
      moment(balanceItem.date, "DD-MMM-YY").toDate(),
      balanceItem.balance,
      `stroke-color: ${this.strokeColor}; fill-color: ${this.fillColor}; fill-opacity: ${fillOpacity}`,
      this.tooltipFormatter(balanceItem),
      isCurrentOrPast
    ];
  }

  // If we have too few dates, google charts will try to interpolate the date value,
  // resulting in duplcated dates on the X axis. If there are too few, we need to set
  // the x-axis labels manually. Otherwise, let google do its thing.
  get xAxisLabels () {
    const distinctDates = _.uniq(this.chartData.map(x => x.date));

    return distinctDates.length >= this.manualXAxisLabelCutoff ?
      null : distinctDates.map(x => moment(x, "DD-MMM-YY").toDate());
  }

  get chartData () {
    return this.hasPredictedData ?
        [ ...this.dataSet, ...this.predictedDataSet ] :
        [ ...this.dataSet ];
  }

  get formattedChartData () {
    const formattedChartPoints = this.chartData.map(this.formatChartDataItem);

    return [
      [
        "Date",
        "Amount",
        { type: "string", role: "style" },
        {
          type: "string", role: "tooltip", p: {
            html: true
          }
        },
        { type: "boolean", role: "certainty" }
      ],
      ...formattedChartPoints
    ];
  }
}
